import {
  Component,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  signal,
  computed,
  input,
  inject,
} from '@angular/core';

import { hasValue } from '@shared-utils';

@Component({
  selector: 'app-v-one-floatlabel',
  imports: [],
  templateUrl: './v-one-floatlabel.component.html',
  styleUrls: ['./v-one-floatlabel.component.scss'],
})
export class VOneFloatlabelComponent implements AfterViewInit, OnDestroy {
  variant = input<'default' | 'on-border'>('default');
  invalid = input<boolean>(false);

  private readonly isFloating = signal(false);
  readonly isFocused = signal(false);

  readonly shouldFloat = computed(() => this.isFloating() || this.isFocused());

  private inputElement: HTMLInputElement | HTMLSelectElement | null = null;
  private labelElement: HTMLLabelElement | null = null;
  private elementRef: ElementRef = inject(ElementRef);

  ngAfterViewInit(): void {
    this.setupFloatingLabel();
    // Check for autofill after a brief delay
    setTimeout(() => this.checkForAutofill(), 100);
  }

  ngOnDestroy(): void {
    this.removeEventListeners();
  }

  private setupFloatingLabel(): void {
    const hostElement = this.elementRef.nativeElement;
    this.inputElement = hostElement.querySelector('input, select');
    this.labelElement = hostElement.querySelector('label');

    if (this.inputElement && this.labelElement) {
      this.checkInitialState();
      this.addEventListeners();

      // Generate unique ID if it doesn't exist
      if (!this.inputElement.id) {
        const randomId =
          'v-one-input-' + Math.random().toString(36).substr(2, 9);
        this.inputElement.id = randomId;
        this.labelElement.setAttribute('for', randomId);
      } else {
        this.labelElement.setAttribute('for', this.inputElement.id);
      }
    }
  }

  private checkInitialState(): void {
    if (this.inputElement) {
      const inputHasValue = hasValue(this.inputElement.value);
      this.isFloating.set(inputHasValue);
    }
  }

  private addEventListeners(): void {
    if (this.inputElement) {
      this.inputElement.addEventListener('focus', this.onFocus.bind(this));
      this.inputElement.addEventListener('blur', this.onBlur.bind(this));
      this.inputElement.addEventListener('input', this.onInput.bind(this));
      // Additional events to detect autofill
      this.inputElement.addEventListener('change', this.onInput.bind(this));
      this.inputElement.addEventListener(
        'animationstart',
        this.onAnimationStart.bind(this),
      );
    }
  }

  private removeEventListeners(): void {
    if (this.inputElement) {
      this.inputElement.removeEventListener('focus', this.onFocus.bind(this));
      this.inputElement.removeEventListener('blur', this.onBlur.bind(this));
      this.inputElement.removeEventListener('input', this.onInput.bind(this));
      this.inputElement.removeEventListener('change', this.onInput.bind(this));
      this.inputElement.removeEventListener(
        'animationstart',
        this.onAnimationStart.bind(this),
      );
    }
  }

  private onFocus(): void {
    this.isFocused.set(true);
    this.isFloating.set(true);
  }

  private onBlur(): void {
    this.isFocused.set(false);
    if (this.inputElement) {
      const inputHasValue = hasValue(this.inputElement.value);
      this.isFloating.set(inputHasValue);
    }
  }

  private onInput(): void {
    if (this.inputElement) {
      const inputHasValue = hasValue(this.inputElement.value);
      this.isFloating.set(inputHasValue || this.isFocused());
    }
  }

  private onAnimationStart(event: Event): void {
    // Detect browser autofill animations
    const animationEvent = event as AnimationEvent;
    if (animationEvent.animationName === 'autofill') {
      this.isFloating.set(true);
    }
  }

  private checkForAutofill(): void {
    if (this.inputElement) {
      // Check if the browser autofilled the field
      const inputHasValue = hasValue(this.inputElement.value);
      if (inputHasValue) {
        this.isFloating.set(true);
      }

      // Check periodically to detect delayed autofill
      const checkInterval = setInterval(() => {
        const currentHasValue = hasValue(this.inputElement?.value);
        if (currentHasValue && !this.isFloating()) {
          this.isFloating.set(true);
          clearInterval(checkInterval);
        }
      }, 100);

      // Clear interval after 3 seconds
      setTimeout(() => clearInterval(checkInterval), 3000);
    }
  }
}
